import { useEffect, useState } from 'react'
import CallCenterTable from './callCenterTable';
import { TableWrapper } from '../utils/CallCenterTableWrapper';
import CallCenterDashboardSharedTables from './CallCenterDashboardSharedTables';
import CallCenterCutOffAndComments from './CallCenterCutOffAndComments';
import { ITable } from '../utils/commonInterfaces';
import helper from '../utils/helper';
import { message } from 'antd';
import { errorMessage } from '../utils/commonStringsConsts';
import tableHeaderConverter, { calculateColumnWidth } from '../utils/tableHeaderConverter';
import { useParams } from 'react-router-dom';
import CallCenterDebtReschedulingTable from './CallCenterDebtReschedulingTable';

export default function CallCenterTransactions() {
    const { userId }: { userId: string } = useParams()
    const [allRequestsData, setAllRequestsData] = useState<ITable>(
        { data: [], columns: [], loading: true, total: 0, currentPage: 1 , perPage: 10 }
    );
    const [userDeductionTableData, setUserDeductionTableData] = useState<ITable>(
        { data: [], columns: [], loading: true, total: 0, currentPage: 1 , perPage: 10  }
    );
    const [adjustmentSummaryTableData, setAdjustmentSummaryTableData] = useState<ITable>(
        { data: [], columns: [], loading: true, total: 0, currentPage: 1, perPage: 10 }
    );
    const [failedTransactionsTableData, setFailedTransactionsTableData] = useState<ITable>(
        { data: [], columns: [], loading: true, total: 0, currentPage: 1, perPage: 10 }
    );
    const [partnerOffersTableData, setPartnerOffersTableData] = useState<ITable>(
        { data: [], columns: [], loading: true }
    );
    const [companyOffersTableData, setCompanyOffersTableData] = useState<ITable>(
        { data: [], columns: [], loading: true }
    );

    const getAllRequests = async (page :number , perPage:number) => {
        try {
            const res: any = await helper.getCallCenterAllRequestsData(Number(userId), page, perPage);
            if (res?.status !== 200) {
                message.error(`${errorMessage} with Requests Data`);
                return
            }
            const data = res?.data?.data?.advances
            const formattedData = data.map((item: any) => ({
                ...item,
                "Amount EGP": Math.trunc(item['Amount EGP']),
                Fees: Math.trunc(item['Fees']),
            }));
            setAllRequestsData((prev) => ({
                ...prev,
                data:formattedData.map((item: any) => ({
                    ...item,
                    advanceID: item?.link ? <a className='text-blue-400 underline' href={item?.link} download target="_blank" rel="noopener noreferrer">{item?.advanceID}</a> : null
                })),
                columns: data?.length > 0 ? tableHeaderConverter(data).filter((column: any) => column.dataIndex !== 'link') : [],
                loading: false,
                total: res?.data?.data?.advancesCount
            }))
        } catch (error) {
            message.error(`${errorMessage} with Requests Data`);
            setAllRequestsData((prev) => ({ ...prev, loading: false }));
        }
    }

    const getUserDeductionsData = async (page :number , perPage:number) => {
        try {
            const res: any = await helper.getCallCenterDeductions(Number(userId), page, perPage);
            if (res?.status !== 200) {
                message.error(`${errorMessage} with Deduction data`);
                return
            }
            const data = res?.data?.data
            setUserDeductionTableData((prev) => ({
                ...prev,
                data,
                columns: data?.length > 0 ? tableHeaderConverter(data) : [],
                loading: false,
                total: res?.data?.total
            }))
        } catch (error) {
            message.error(`${errorMessage} with Deduction data`);
            setUserDeductionTableData((prev) => ({ ...prev, loading: false }));
        }
    }

    const getAdjustmentsSummaryData = async (page: number, perPage: number) => {
        try {
            const res: any = await helper.getCallCenterAdjustmentSummary(Number(userId), page, perPage);
            if (res?.status !== 200) {
                message.error(`${errorMessage} with adjustments summary data`);
                return
            }
            const data = res?.data?.data
            const formattedData = data.map((item: any) => ({
                ...item,
                adjustmentDate: helper.formatDate(item['adjustmentDate'], 'YYYY/MM/DD HH:mm:ss'),
                adjustedMonth: helper.formatDate(item['adjustedMonth'], 'MMM-YYYY'),
                adjustedAmount: Math.trunc(item['adjustedAmount']),
            }));
            setAdjustmentSummaryTableData((prev) => ({
                ...prev,
                data: formattedData.map((item: any) => ({
                    ...item,
                    link: item?.link ? <a className='text-blue-400' href={item?.link} download target="_blank" rel="noopener noreferrer">Click Here</a> : null
                })),
                columns: data?.length > 0 ? tableHeaderConverter(data) : [],
                loading: false,
                total: res?.data?.total
            }))
        } catch (error) {
            message.error(`${errorMessage} with adjustments summary data`);
            setAdjustmentSummaryTableData((prev) => ({ ...prev, loading: false }));
        }
    }

    const getUserFailedTransactionsData = async (page: number, perPage: number) => {
        try {
            const res: any = await helper.getCallCenterFailedTransactions(Number(userId), page, perPage);
            if (res?.status !== 200) {
                message.error(`${errorMessage} with failed transactions data`);
                return
            }
            const data = res?.data?.data
            const formattedData = data.map((item: any) => ({
                ...item,
                date: helper.formatDate(item['date'], 'MMMM D, YYYY, hh:mm A')
            }));
            setFailedTransactionsTableData((prev) => ({
                ...prev,
                data: formattedData,
                columns: data?.length > 0 ? tableHeaderConverter(data) : [],
                loading: false,
                total: res?.data?.total
            }))
        } catch (error) {
            message.error(`${errorMessage} with failed transactions data`);
            setFailedTransactionsTableData((prev) => ({ ...prev, loading: false }));
        }
    }

    const getPartnerOffersData = async () => {
        try {
            const res: any = await helper.getCallCenterPartnerOffers(Number(userId));
            if (res?.status !== 200) {
                message.error(`${errorMessage} with partner offers data`);
                return
            }
            const data = res?.data?.data
            const formattedData = data.map((item: any) => ({
                ...item,
                startDate: helper.formatDate(item['startDate'], 'MMM D, YYYY HH:mm A'),
                endDate: helper.formatDate(item['endDate'], 'MMM D, YYYY HH:mm A'),
                createdAt: helper.formatDate(item['createdAt'], 'MMM D, YYYY HH:mm A'),
                suspendedAt: item['suspendedAt'] ? helper.formatDate(item['suspendedAt'], 'MMM D, YYYY HH:mm A') : '',
            }));
            setPartnerOffersTableData((prev) => ({
                ...prev,
                data: formattedData,
                columns: data?.length > 0 ? tableHeaderConverter(data).map((column: any) => ({
                    ...column,
                    width: calculateColumnWidth(helper.unCamelCase(column.title), column.dataIndex, formattedData),
                    ellipsis: { showTitle: true },
                })) : [],
                loading: false,
            }))
        } catch (error) {
            message.error(`${errorMessage} with partner offers data`);
            setPartnerOffersTableData((prev) => ({ ...prev, loading: false }));
        }
    }

    const getCompanyOffersData = async () => {
        try {
            const res: any = await helper.getCallCenterCompanyOffers(Number(userId));
            if(res?.status !== 200) {
                message.error(`${errorMessage} with company offers data`);
                return
            }
            const data = res?.data?.data
            const formattedData = data.map((item: any) => ({
                ...item,
                offerStartDate: helper.formatDate(item['offerStartDate'], 'MMM D, YYYY HH:mm A'),
                offerEndDate: helper.formatDate(item['offerEndDate'], 'MMM D, YYYY HH:mm A'),
                suspendedAt: item['suspendedAt'] ? helper.formatDate(item['suspendedAt'], 'MMM D, YYYY HH:mm A') : '',
                offerCreationDate: helper.formatDate(item['offerCreationDate'], 'MMM D, YYYY HH:mm A'),
            }));
            setCompanyOffersTableData((prev) => ({
                ...prev,
                data: formattedData,
                columns: data?.length > 0 ? tableHeaderConverter(data).map((column: any) => ({
                    ...column,
                    width: calculateColumnWidth(helper.unCamelCase(column.title), column.dataIndex, formattedData),
                })) : [],
                loading: false,
            }))
        } catch (error) {
            message.error(`${errorMessage} with company offers data`);
            setCompanyOffersTableData((prev) => ({ ...prev, loading: false }));
        }
    }
    useEffect(() => {
        getAllRequests(allRequestsData?.currentPage as number, allRequestsData?.perPage as number)
        getUserDeductionsData(userDeductionTableData?.currentPage as number, userDeductionTableData?.perPage as number);
        getAdjustmentsSummaryData(adjustmentSummaryTableData?.currentPage as number, adjustmentSummaryTableData?.perPage as number);
        getUserFailedTransactionsData(failedTransactionsTableData?.currentPage as number, failedTransactionsTableData?.perPage as number);
        getCompanyOffersData();
        getPartnerOffersData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableChange = async (tableType: 'allRequests' | 'userDeductions' | 'adjustmentsSummary' | 'failedTransactions', { current, pageSize }: { current: number, pageSize: number }) => {
        switch (tableType) {
            case 'allRequests':
                setAllRequestsData((prev) => ({ ...prev, currentPage: current, perPage: pageSize, loading: true }));
                await getAllRequests(current, pageSize as number);
                break;
            case 'userDeductions':
                setUserDeductionTableData((prev) => ({ ...prev, currentPage: current, perPage: pageSize, loading: true }));
                await getUserDeductionsData(current, pageSize as number);
                break;
            case 'adjustmentsSummary':
                setAdjustmentSummaryTableData((prev) => ({ ...prev, currentPage: current, perPage: pageSize, loading: true }));
                getAdjustmentsSummaryData(current, pageSize as number);
                break;
            case 'failedTransactions':
                setFailedTransactionsTableData((prev) => ({ ...prev, currentPage: current, perPage: pageSize, loading: true }));
                getUserFailedTransactionsData(current, pageSize as number);
                break;
            default:
                break;
        }
    }

    return (
        <>
            <div className="bg-white callCenterMainDashboardHeader">Call Center Dashboard - Transactions</div>
            <div className="callCenterMainDashboardContainer">
                {/* <CallCenterDashboardFilter /> */}
                <CallCenterDashboardSharedTables />
                <TableWrapper title='Requests:'>
                    <CallCenterTable
                        columns={allRequestsData?.columns}
                        rows={allRequestsData?.data}
                        pagination={{ current: allRequestsData?.currentPage, pageSize: allRequestsData?.perPage, total: allRequestsData?.total }}
                        loading={allRequestsData?.loading}
                        handleTableChange={(page: { current: number, pageSize: number }) => handleTableChange('allRequests', page)}
                        scrolling={allRequestsData?.data?.length ? { x: 'max-content' } : false}
                        title='Call Center: All Requests by phone or RequestID'
                    />
                </TableWrapper>
                <TableWrapper title='Deductions per month: '>
                    <CallCenterTable
                        columns={userDeductionTableData?.columns}
                        rows={userDeductionTableData?.data}
                        pagination={{ current: userDeductionTableData?.currentPage, pageSize: userDeductionTableData?.perPage, total: userDeductionTableData?.total }}
                        handleTableChange={(page: { current: number, pageSize: number }) => handleTableChange('userDeductions', page)}
                        loading={userDeductionTableData?.loading}
                        scrolling={userDeductionTableData?.data?.length ? { x: 'max-content' } : false}
                        title='User Deductions per month - Call center'
                    />
                </TableWrapper>
                <TableWrapper title='Adjustment Summary:'>
                    <CallCenterTable
                        columns={adjustmentSummaryTableData?.columns}
                        rows={adjustmentSummaryTableData?.data}
                        handleTableChange={(page: { current: number, pageSize: number }) => handleTableChange('adjustmentsSummary', page)}
                        pagination={{ current: adjustmentSummaryTableData?.currentPage, pageSize: adjustmentSummaryTableData?.perPage, total: adjustmentSummaryTableData?.total }}
                        loading={adjustmentSummaryTableData?.loading}
                        scrolling={adjustmentSummaryTableData?.data?.length ? { x: `max-content`, y: 240 } : { y: 240 }}
                        title='Adjustment Summary - Call center'
                    />
                </TableWrapper>
                <TableWrapper title='Failed Transactions:'>
                    <CallCenterTable
                        columns={failedTransactionsTableData?.columns}
                        rows={failedTransactionsTableData?.data}
                        handleTableChange={(page: { current: number, pageSize: number }) => handleTableChange('failedTransactions', page)}
                        pagination={{ current: failedTransactionsTableData?.currentPage, pageSize: failedTransactionsTableData?.perPage, total: failedTransactionsTableData?.total }}
                        loading={failedTransactionsTableData?.loading}
                        scrolling={failedTransactionsTableData?.data?.length ? { x: `max-content`, y: 240 } : { y: 240 }}
                        title='Failed Transactions - Call center'
                    />
                </TableWrapper>
                <TableWrapper title='Company Offers:'>
                    <CallCenterTable
                        columns={companyOffersTableData?.columns}
                        rows={companyOffersTableData?.data}
                        loading={companyOffersTableData?.loading}
                        pagination={false}
                        scrolling={companyOffersTableData?.data?.length ? { x: `max-content`, y: 240 } : { y: 240 }}
                        title='Company Offers - Call center'
                    />
                </TableWrapper>
                <TableWrapper title='Partner Offers:'>
                    <CallCenterTable
                        columns={partnerOffersTableData?.columns}
                        rows={partnerOffersTableData?.data}
                        loading={partnerOffersTableData?.loading}
                        pagination={false}
                        scrolling={partnerOffersTableData?.data?.length ? { x: `max-content`, y: 240 } : { y: 240 }}
                        title='Partner Offers - Call center'
                    />
                </TableWrapper>
                <CallCenterCutOffAndComments />
                <CallCenterDebtReschedulingTable />
            </div>
        </>
    );
}


